/* eslint-disable max-lines */
import { Injectable } from '@angular/core';
import { BaseFormService } from '../base';
import {
  CELL_RENDERER,
  CreateFormRules,
  ENTITY_TYPES,
  GridRowData,
  GRID_LIST_VIEW_TYPE,
  IBaseEntityResponse,
  IBaseFormService,
  IGridFlatInventoryCountItemTotal,
  IGridInventoryCountItemTotalData,
  IGridInventoryCountItemTotalFormData,
  IInventoryCountFormFieldPath,
  IInventoryCountGroup,
  IInventoryCountItem,
  IInventoryCountItemChild,
  IQohInventoryItemVenue,
  IQuantityOnHandItem,
  IQuantityOnHandResponse,
  ISite,
  ISiteInventoryCountGroup,
  ITransferErrorMapper,
  ITransferItem,
  IXVIListColumn,
  ObjectId,
  SelectOption,
  USER_CONFIG_MODULE_NAME,
  IUserSetting,
  USER_CONFIG_MODULE_TYPE,
  IUserConfig,
  IInventoryItemVenue,
  IInventoryCountOverrideValueOptions,
  IInventoryCountValue,
  IModalDialogResult,
  IApiListResponse,
  MultiplyCountUnit,
} from '../../types';
import { FormGroup } from '@angular/forms';
import {
  observableCombineLatest,
  observableOf,
  rx,
  Subscription,
} from '../../rx';
import { createFormRules as createInventoryCountFormRules } from '@shared/services/inventory-count/form-rules';
import {
  assign,
  cloneDeep,
  filter,
  find,
  forEach,
  flatMapDeep,
  get,
  groupBy,
  head,
  isEmpty,
  isNil,
  isUndefined,
  map,
  reduce,
  some,
  sortBy,
  toPairs,
  toLower,
  isEqual,
  size,
  has,
  set,
  includes,
  unset,
  uniqBy,
  isObject,
  gt,
} from 'lodash';
import {
  CompanyUtilService,
  InventoryCountGroupService,
  InventoryCountService,
  NumberFormatService,
  PermissionService,
  QohService,
  ScanningService,
  SitesService,
  STATE_KEYS,
} from '@shared/services';
import { ENTITY_ID_PATH, MAX_URL_QUERY_NUMBER } from '@config/global-constants';
import {
  INVENTORY_COUNT_ITEM_STATUS_VALUE,
  INVENTORY_COUNT_OVERRIDE_VALUE_TYPES,
  INVENTORY_COUNT_STATUS_VALUE,
  INVENTORY_COUNT_UNIT_VALUE,
} from '@shared/types/enums/inventory-count.enum';
import { lazyInject } from '@containers/base/decorators';
import {
  generateId,
  generateObjectId,
  setGridKeyboardSupport,
  toDate,
} from '@shared/utils';
import { BehaviorSubject, Observable, ObservableInput } from 'rxjs';
import {
  getFormControl,
  getFormFieldValue,
  setFormFieldValue,
} from '@components/form/services/schema-form/schema-form-functions';
import {
  ITreeConverterConfig,
  ITreeData,
} from '@shared/types/common/tree.type';
import { InventoryItemVenueService } from '@shared/services/inventory-item-venue';
import { UiActions } from '@store/actions';
import {
  CellClassParams,
  ColDef,
  ColumnApi,
  GridApi,
  ICellRendererParams,
  RowNode,
  ValueFormatterParams,
} from 'ag-grid-community';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { XuxGridAction, XuxGridActionPayload } from '@shared/types';
import { XuxEditGridComponent } from '@components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { DecimalNumber } from '@app/shared/common';
import {
  inventoryCountListColumns,
  inventoryCountTreeColumns,
} from './inventory-count.columns';
import { getAllRowsAfterFilterAndSort } from '@app/components/xvi-grid/custom-components/cell-renderer-components/renderer-helpers';
import { ColumnState } from 'ag-grid-community/dist/lib/columnController/columnController';
import { select } from '@angular-redux/store';
import { SELECTABLE_COLUMNS } from './inventory-count.constants';
import { findColumnConfig, flattenItems } from './inventory-count.util';
import { clearCountModalOptions } from './clear-count-modal.definition';
import {
  InventoryCountCommonGridRow,
  InventoryCountGridMeasure,
} from './inventory-count.type';
import { InventoryItemVenueMeasure } from '@geniuspos/xenial-data-contracts/types/dm-entities/inventory-item-venue/inventory-item-venue';
import { I18n } from '@app/i18n/strings-keys';
import { INTERNAL_GRID_ROW_ID } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component.constants';

@Injectable()
export class InventoryCountFormService
  extends BaseFormService
  implements IBaseFormService<ENTITY_TYPES.INVENTORY_COUNT>
{
  public static ENTITY_TYPE: ENTITY_TYPES = ENTITY_TYPES.INVENTORY_COUNT;
  @lazyInject(CompanyUtilService) public companyUtilService: CompanyUtilService;
  @lazyInject(InventoryItemVenueService)
  public inventoryItemVenueService: InventoryItemVenueService;
  @lazyInject(InventoryCountGroupService)
  public inventoryCountGroupService: InventoryCountGroupService;
  @lazyInject(InventoryCountService)
  public inventoryCountService: InventoryCountService;
  @lazyInject(QohService) public qohService: QohService;
  @lazyInject(UiActions) public uiActions: UiActions;
  @lazyInject(NumberFormatService)
  public numberFormatService: NumberFormatService;
  @lazyInject(SitesService) public sitesService: SitesService;
  @lazyInject(PermissionService) public permissionService: PermissionService;
  @lazyInject(ScanningService) public scanningService: ScanningService;

  @select(STATE_KEYS.USER_CONFIG)
  public userConfig$: Observable<any>;
  public form: FormGroup;
  public createFormRulesFunction: CreateFormRules<InventoryCountFormService> =
    createInventoryCountFormRules;
  public inventoryCountGroup: IInventoryCountGroup;
  public site: ISite;
  public listGrid: XuxEditGridComponent;

  public formFieldPath: IInventoryCountFormFieldPath = {
    startDate: 'start_date',
    status: 'status',
    siteId: 'site_id',
    inventoryCountGroupEntityId: 'inventory_count_group_entity_id',
    name: 'name',
    date: 'date',
    inventoryItemVenues: 'inventory_items',
    inventoryCountItems: 'inventory_count_items',
    virtualInventoryCountItems: 'virtual.inventory_count_items',
    countedValue: 'counted_value',
    extendedValue: 'extended_value',
    varianceCost: 'variance_cost',
    virtualViewSetting: 'virtual.view_setting',
    virtualSetCountUnit: 'virtual.set_count_unit',
  };

  public isInventoryItemVenuesGridBusy$: BehaviorSubject<boolean> =
    new BehaviorSubject(false);
  public siteAndInventoryCountGroup$: BehaviorSubject<ISiteInventoryCountGroup> =
    new BehaviorSubject({});

  public majorItems$: BehaviorSubject<SelectOption[]> = new BehaviorSubject([]);
  public minorItems$: BehaviorSubject<SelectOption[]> = new BehaviorSubject([]);

  protected subscriptions: Subscription[] = [];

  public curDisplayItems: RowNode[] = [];
  public listViewItems: ITreeData[] = [];
  public columnApi: ColumnApi;
  public listGridApi: GridApi;
  public totalName: string = 'Total';
  public columns$: BehaviorSubject<IXVIListColumn[]> = new BehaviorSubject(
    this.getInventoryCountColumns(),
  );
  public isDisableGridButtons$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );
  public isFinalStatus$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  public isDisableClearButtons$: BehaviorSubject<boolean> = new BehaviorSubject(
    false,
  );

  private eachOne: string = 'Each [1]';

  public get userViewConfig(): IUserConfig {
    return this.stateService.getUserConfig(
      USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
      USER_CONFIG_MODULE_TYPE.INVENTORY_COUNT,
    );
  }

  public get canEdit(): boolean {
    return (
      (this.inventoryCountService.canManage && this.isEditMode()) ||
      (this.inventoryCountService.canAdd && this.isNewMode())
    );
  }

  protected applyColumnState(): void {
    try {
      this.subscriptions.push(
        this.userConfig$.subscribe((allModuleColumnConfig) => {
          // For save column state
          this.allModuleColumnConfig = allModuleColumnConfig;
        }),
      );
      const columnState = get(
        this.userViewConfig,
        'module_config.alphabetical_list_view',
        [],
      );
      if (!this.isCategoryListView() && isEmpty(columnState)) {
        setTimeout(() => {
          this.listGridApi?.sizeColumnsToFit();
        }, 0);
      }
      if (!this.isCategoryListView() && !isEmpty(columnState)) {
        const columns = this.getInventoryCountColumns();
        // use the selected column to update the column state
        forEach(columns, (col: IXVIListColumn) => {
          col.canSelected = includes(SELECTABLE_COLUMNS, col.path);
          const selectedColState = find(columnState, (colState: ColumnState) =>
            isEqual(colState.colId, col.path),
          );

          if (!isNil(selectedColState)) {
            col.hide = selectedColState.hide;
          }

          if (!isNil(col.filterParams)) {
            col.filterParams = {
              ...col.filterParams,
              quickFilter:
                includes(SELECTABLE_COLUMNS, col.path) && col.hide
                  ? false
                  : true,
            };
          }
          col.selected =
            includes(SELECTABLE_COLUMNS, col.path) && !selectedColState.hide;
        });
        setTimeout(() => {
          const sortedColumns = [];
          const localColumnConfig = this.columnApi.getColumnState();
          // use the local column state and merge the remote column state in order to get new order column state
          forEach(
            this.autoComplementColumns(localColumnConfig, columnState),
            (colState: ColumnState) => {
              const selectedColumn = find(columns, (column: IXVIListColumn) =>
                isEqual(colState.colId, column.path),
              );
              if (!isNil(selectedColumn)) {
                sortedColumns.push(selectedColumn);
              }
            },
          );
          this.columns$.next(sortedColumns);
          this.columnApi.setColumnState(columnState as ColumnState[]);
        }, 100);
      }
    } catch (error) {
      this.uiActions.disableLoader();
    } finally {
      this.uiActions.preventDisableLoaderAfterRequestFinish(false);
      this.uiActions.preventLoaderCloseWhenTimeout(false);
      this.uiActions.disableLoader();
    }
  }

  public initKeyDownEventSubscription(): void {
    this.scanningService.scanOptions = {
      isCategoryListView: this.isCategoryListView(),
      focusKeyForListModal: 'measure.count',
      focusKeyForTreeModal: 'count',
      isCompleted: this.isClosedStatus(),
      focusOnNextRow: true,
    };
    const scanningSubscription =
      this.scanningService.returnScanningSubscription(
        this.scanningService.scanOptions,
      );
    this.subscriptions.push(scanningSubscription);
  }

  /**
   * Diff local and db column state, and combine it, such as add new column by form rules
   *
   * @param localColumnState Local column state from form rules config
   * @param apiColumnState DB column state
   * @returns Combine column state
   */
  public autoComplementColumns(
    localColumnState: ColumnState[],
    apiColumnState: ColumnState[],
  ): ColumnState[] {
    const apiColumnKeys = map(
      apiColumnState,
      (column: ColumnState) => column.colId,
    );

    forEach(localColumnState, (localColumn: ColumnState, index: number) => {
      if (!includes(apiColumnKeys, localColumn.colId)) {
        apiColumnState.splice(index, 0, localColumn);
      }
    });

    return apiColumnState;
  }

  public destroyFormDefinition(): void {
    this.isFinalStatus$.next(false);
    this.scanningService.destroyServiceDefinition();
    super.destroyFormDefinition();
  }

  public onFormReady(form: FormGroup): void {
    this.form = form;
    this.initInventoryCountSubscriptions();
    this.initListTotalsSubscription();
    super.onFormReady(form);
    this.initInventoryCountSubscription();
    this.initCountUnitSubscription();
    this.initKeyDownEventSubscription();
    this.eachOne = `${this.i18nService.instant(I18n.Common.Each)} [1]`;
    this.totalName = this.i18nService.instant(I18n.Common.Total);
  }

  public initInventoryCountSubscription(): void {
    const subscription = this.fieldValueChanges(
      this.formFieldPath.inventoryCountItems,
    ).subscribe((inventoryList: InventoryCountCommonGridRow[]) => {
      let listItems = [];

      if (this.isCategoryListView()) {
        listItems = cloneDeep(
          flattenItems(
            inventoryList as unknown as Partial<IInventoryItemVenue>[],
          ),
        );
      } else {
        listItems = map(uniqBy(inventoryList, 'entity_id'));
      }
      const changedItemsControl = this.getFormFieldControl(
        this.formFieldPath.inventoryCountItems,
      );
      const reallyChangedItems = this.getReallyChangeGridItems(listItems);
      const isCountChange = !isEmpty(reallyChangedItems);

      if (isCountChange) {
        this.inventoryCountService.reallyChangedInventoryItems =
          cloneDeep(reallyChangedItems);
      } else {
        setTimeout(() => {
          changedItemsControl.markAsPristine();
          this.form.markAsPristine();
        }, 0);
      }
    });
    this.subscriptions.push(subscription);
  }

  public initCountUnitSubscription(): void {
    const subscription = this.fieldValueChanges(
      this.formFieldPath.virtualSetCountUnit,
    )
      .pipe(rx.distinctUntilChanged(isEqual))
      .subscribe((countUnit: INVENTORY_COUNT_UNIT_VALUE) => {
        setTimeout(() => {
          this.getFormFieldControl(
            this.formFieldPath.virtualSetCountUnit,
          ).markAsPristine();
        }, 0);
        const itemsDirty: boolean = getFormControl(
          this.form,
          this.formFieldPath.inventoryCountItems,
        ).dirty;
        let items = getFormFieldValue(
          this.form,
          this.formFieldPath.inventoryCountItems,
        );

        if (this.isCategoryListView()) {
          forEach(items, (item) => {
            forEach(item.children, (child) => {
              set(
                child,
                'children',
                this.reCalcDataWhenChangeCountUnit(child.children, countUnit),
              );
            });
          });
        } else {
          items = this.reCalcDataWhenChangeCountUnit(items, countUnit);
        }

        setFormFieldValue(
          this.form,
          this.formFieldPath.inventoryCountItems,
          items,
          itemsDirty,
        );

        if (this.isCategoryListView()) {
          setTimeout(() => {
            this.listGridApi.expandAll();
          });
        }
      });
    this.subscriptions.push(subscription);
  }

  public reCalcDataWhenChangeCountUnit(
    items: IQohInventoryItemVenue[],
    countUnit: INVENTORY_COUNT_UNIT_VALUE,
  ): IQohInventoryItemVenue[] {
    return map(items, (item) => {
      const countUnitValue = get(item, countUnit);
      let factorValue: number = 1;

      if (isObject(countUnitValue)) {
        factorValue = get(countUnitValue, 'factor_value');
      }

      const inventoryItemCost = this.getInventoryItemCost(null, item).mul(
        factorValue,
      );
      const managerCount = this.calculateManagerCountForUnitCount(
        item,
        factorValue,
      );

      const qoh: number = this.isClosedStatus()
        ? get(
            item,
            'origin_system_count', // completed status (qoh)
          )
        : get(
            find(
              get(item, 'qohItems'),
              (qohItem) => qohItem?.site_id === this.site?.id,
            ),
            'count', // non-completed status (qoh)
            0,
          );
      const systemCount = new DecimalNumber(qoh).div(factorValue);

      const hasCount = some(
        this.isCategoryListView() ? get(item, 'children') : item?.measures,
        (measure) => !isNil(measure.count) && Number(measure.count) >= 0,
      );

      const varianceQuantity = managerCount.sub(systemCount);

      return assign({}, item, {
        uom: get(item, countUnit),
        inventory_item_cost: inventoryItemCost,
        system_count: systemCount,
        manager_count: managerCount,
        variance_quantity: hasCount ? varianceQuantity : 0,
      });
    });
  }

  public calculateManagerCountForUnitCount(
    item: IQohInventoryItemVenue,
    factor: number = 1,
  ): DecimalNumber {
    let measures = [];
    if (this.isClosedStatus()) {
      measures = get(item, 'counts', []);
    } else {
      if (this.isCategoryListView()) {
        measures = get(item, 'children', []);
      } else {
        measures = get(item, 'measures', []);
      }
    }

    const factorValues = map(
      measures,
      (measure) => measure.factor_value * (measure?.count || 0),
    );

    return factorValues
      .reduce((partialSum, a) => partialSum.add(a), new DecimalNumber(0))
      .div(factor);
  }

  /**
   * handle when list grid is ready
   *
   * @param action xux grid action
   * @param grid edit grid object
   */
  public handleListGridReady(
    action: XuxGridAction,
    grid: XuxEditGridComponent,
    permissions?: {
      canEdit: () => boolean;
    },
  ): void {
    this.listGrid = grid;
    this.listGridApi = action.payload?.gridApi;
    this.columnApi = action?.payload?.gridApi?.['columnController']?.columnApi;
    this.scanningService.gridApi = this.listGridApi;
    this.scanningService.columnApi = this.columnApi;

    if (!permissions?.canEdit() || this.isClosedStatus()) {
      // disable arrow key
      setGridKeyboardSupport(grid.xuxGridComponent, false);
    }
  }

  public handleSortingChange(
    action: XuxGridAction,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    grid: XuxEditGridComponent,
  ): void {
    this.inventoryCountService.tableSortConfig = action.payload.sortModel;
    this.inventoryCountService.isSortChangeFromGrid = true;
  }

  public handleColumnSelectChange(action: XuxGridAction): void {
    this.saveUserConfig(
      USER_CONFIG_MODULE_NAME.INVENTORY_COUNT_VIEW_CONFIG,
      action.payload.selectedColumns,
    );
    this.applyColumnState();
  }

  public initInventoryCountSubscriptions(): void {
    const inventoryCountGroupState = get(
      this.stateService.getByPath(STATE_KEYS.UI_EDITOR),
      'options.state',
      {},
    );
    this.uiActions.preventDisableLoaderAfterRequestFinish(true);
    this.uiActions.preventLoaderCloseWhenTimeout(true);
    this.uiActions.enableLoader();
    if (isEmpty(inventoryCountGroupState)) {
      this.fetchSiteAndInventoryCountGroup()
        .pipe(
          rx.map((inventoryCountGroupResult) => {
            this.setCurrentSiteAndInventoryCountGroup(
              inventoryCountGroupResult,
            );
            this.initFormFieldValues();
          }),
          rx.mergeMap(() =>
            // InventoryCountGroupService.isCompanyLevel is a static property set by
            // InventoryCountsGroupPageModule constructor (via fetchPhysicalCountMeasureConfig).
            // It is always initialised before the user can navigate here from the group page,
            // so reading it synchronously here is safe for all supported navigation paths.
            this.getInventoryItemVenues(this.site?.id),
          ),
        )
        .subscribe((items: IQohInventoryItemVenue[]) => {
          this.updateInventoryItemVenues(items);
        });

      return;
    }

    this.setCurrentSiteAndInventoryCountGroup(inventoryCountGroupState);
    this.initFormFieldValues();
    // See comment above: isCompanyLevel is set by the group-page module before this runs.
    this.getInventoryItemVenues(this.site?.id).subscribe(
      (items: IQohInventoryItemVenue[]) => {
        this.updateInventoryItemVenues(items);
      },
    );
  }

  public setCurrentSiteAndInventoryCountGroup(inventoryCountGroupState): void {
    this.site = inventoryCountGroupState?.site || ({} as ISite);
    this.inventoryCountGroup =
      inventoryCountGroupState?.inventoryCountGroup ||
      ({} as IInventoryCountGroup);
    this.siteAndInventoryCountGroup$.next(inventoryCountGroupState);
  }

  public initFormFieldValues(): void {
    if (this.isNewMode()) {
      const currentDate = toDate().toISOString();

      setFormFieldValue(
        this.form,
        this.formFieldPath.name,
        this.inventoryCountGroup?.name || '',
        false,
      );
      setFormFieldValue(
        this.form,
        this.formFieldPath.startDate,
        currentDate,
        false,
      );
      setFormFieldValue(this.form, this.formFieldPath.date, currentDate, false);
      setFormFieldValue(
        this.form,
        this.formFieldPath.status,
        INVENTORY_COUNT_STATUS_VALUE.Open,
        false,
      );
      setFormFieldValue(
        this.form,
        this.formFieldPath.siteId,
        this.site?.id || '',
        false,
      );
      setFormFieldValue(
        this.form,
        this.formFieldPath.inventoryCountGroupEntityId,
        this.inventoryCountGroup?.entity_id || '',
        false,
      );

      this.form.markAsDirty();
    }
  }

  public getListGridRowCellClass(): Record<string, (params: any) => boolean> {
    return {
      'xvi-grid-warn-row': (params): boolean => {
        return get(params, 'data.error');
      },
      'inventory-item-group-row': (params): boolean => {
        return get(params, 'data.isInventoryItemGroupRow', false);
      },
      'measure-row': (params): boolean => {
        return get(params, 'data.isMeasureRow', false);
      },
      'minor-category-row': (params): boolean => {
        return get(params, 'data.isMinorCategoryRow', false);
      },
      'major-category-row': (params): boolean => {
        return !get(params, 'data.isMinorCategoryRow', false);
      },
      'data-pinned-bottom': (params: CellClassParams): boolean =>
        params.node.isRowPinned(),
      'leaf-measure-row': (params): boolean => params.data.isLeaf,
      'scanning-highlight': (params: CellClassParams): boolean => {
        const rowData = get(params, 'data');

        return includes(
          [
            get(rowData, ENTITY_ID_PATH),
            get(rowData, 'inventory_item_entity_id'),
          ],
          this.scanningService.scannedItemId,
        );
      },
    };
  }

  public getInventoryItemVenues(
    siteId: ObjectId,
  ): Observable<IQohInventoryItemVenue[]> {
    this.isInventoryItemVenuesGridBusy$.next(true);
    const getRequiredFields: string[] = ['measures', 'upc_codes'];

    if (this.isClosedStatus()) {
      const virtualInventoryItemVenues = getFormFieldValue(
        this.form,
        this.formFieldPath.virtualInventoryCountItems,
      );

      const inventoryItemIds: ObjectId[] = map(
        virtualInventoryItemVenues,
        'inventory_item_entity_id',
      );

      return this.inventoryItemVenueService
        .fetchList(
          {
            filter: gt(size(inventoryItemIds), MAX_URL_QUERY_NUMBER)
              ? 'is_active eq true'
              : `entity_id eq in(${inventoryItemIds.join()})`,
            select: [ENTITY_ID_PATH, ...getRequiredFields],
          },
          { fetchFullListData: true },
        )
        .pipe(
          rx.map((res: IApiListResponse) => {
            const measureHashObj = {};
            const upcCodesHashObj = {};

            forEach(res.items, (item) => {
              set(
                measureHashObj,
                get(item, ENTITY_ID_PATH),
                get(item, 'measures'),
              );
              set(
                upcCodesHashObj,
                get(item, ENTITY_ID_PATH),
                get(item, 'upc_codes'),
              );
            });

            return { measureHashObj, upcCodesHashObj };
          }),
          rx.map(({ measureHashObj, upcCodesHashObj }) => {
            return map(virtualInventoryItemVenues, (virtualItem) => {
              const inventoryItemCost = this.getInventoryItemCost(virtualItem);
              const managerCount = this.calculateManagerCount(virtualItem);
              const systemCount = get(virtualItem, 'system_count', 0);
              const varianceQuantity = get(virtualItem, 'variance_count', 0);

              const varianceCost = get(virtualItem, 'variance_cost', 0);
              const measures = get(virtualItem, 'counts', []);
              const extendedValue = new DecimalNumber(systemCount).mul(
                inventoryItemCost,
              );
              const countedValue = new DecimalNumber(managerCount).mul(
                inventoryItemCost,
              );

              // delete count property since we don't need to display the value in this grid cell
              delete virtualItem.count;
              const itemMeasures = get(
                measureHashObj,
                get(virtualItem, 'inventory_item_entity_id'),
              );

              const upc_codes = get(
                upcCodesHashObj,
                get(virtualItem, 'inventory_item_entity_id'),
              );

              return assign({}, virtualItem, {
                measures,
                upc_codes,
                ...this.returnMultiplyCountUnit(itemMeasures),
                inventory_item_cost: inventoryItemCost,
                system_count: systemCount,
                origin_system_count: systemCount,
                manager_count: managerCount,
                counted_value: countedValue,
                extended_value: extendedValue,
                variance_cost: varianceCost,
                variance_quantity: varianceQuantity,
              });
            });
          }),
        );
    }

    const query = {
      select: [
        'code',
        'reporting_category_major',
        'reporting_category_minor',
        'name',
        'description',
        'costs',
        'site_id',
        ...(!InventoryCountGroupService.isCompanyLevel
          ? ['physical_count_measures']
          : []),
        ...getRequiredFields,
      ],
      filter: InventoryCountGroupService.isCompanyLevel
        ? 'measures/transactions/physical_count eq in(true)'
        : 'physical_count_measures/factor_value gt 0',
    };

    return this.inventoryItemVenueService
      .fetchCurrentList(query, [siteId])
      .pipe(
        rx.switchMap((res: IBaseEntityResponse) => {
          if (isEmpty(res.items)) return observableOf([]);

          return this.getQuantityOnHandData(
            res.items as IQohInventoryItemVenue[],
            siteId,
          );
        }),
      );
  }

  /**
   * Filter measures based on physical_count flag in transactions
   *
   * @param itemMeasures - All measures from inventory item
   * @returns Filtered measures where transactions.physical_count is true
   */
  public filterMeasuresByPhysicalCount(
    itemMeasures: InventoryItemVenueMeasure[],
  ): InventoryItemVenueMeasure[] {
    return filter(itemMeasures, { transactions: { physical_count: true } });
  }

  public returnMultiplyCountUnit(
    itemMeasures: InventoryItemVenueMeasure[],
  ): MultiplyCountUnit {
    const baseMeasure =
      (find(itemMeasures, { is_base: true }) as InventoryItemVenueMeasure) ||
      this.eachOne;

    const defaultTransfer =
      find(itemMeasures, 'default_unit.transfer') || this.eachOne;
    const defaultOrder =
      find(itemMeasures, 'default_unit.delivery') || this.eachOne;

    return {
      base_unit: baseMeasure,
      default_transfer_unit: defaultTransfer,
      default_ordering_unit: defaultOrder,
      uom: isObject(baseMeasure)
        ? `${get(baseMeasure, 'description')} [${this.numberFormatService.getNumberFormatter(false)({ value: get(baseMeasure, 'factor_value') })}]`
        : this.eachOne,
    };
  }

  public getSortedTreeDataByConfig(
    data: IQohInventoryItemVenue[] | ITransferItem[],
    config: ITreeConverterConfig,
  ): ITreeData[] {
    // The path used for sorting and grouping
    const path = config.path;
    // The groupName used for group naming
    const groupName = config.groupName;
    const majorAlias = config.majorAlias || {};
    const minorAlias = config.minorAlias || {};

    const sortData = sortBy(
      map(data, (item) => {
        return assign(
          {},
          item,
          {
            [majorAlias.backup]: item[majorAlias.origin],
            [minorAlias.backup]: item[minorAlias.origin],
            internal_grid_row_id: `local${generateId()}`,
            ...(config.additional || {}),
          },
          config.additionalFn ? config.additionalFn(item) : {},
        );
      }),
      (item) => toLower(get(item, path, '')),
    );

    if (!groupName) return sortData;

    return map(toPairs(groupBy(sortData, path)), ([name, children]) => {
      const childrenConfig = config.childrenConfig;
      const newChildren = this.getSortedTreeDataByConfig(
        children,
        childrenConfig,
      );
      if (path === 'name') {
        const counts: IInventoryCountItemChild[] = head(newChildren)?.counts
          ? head(newChildren)?.counts
          : head(newChildren)?.measures;

        return {
          ...newChildren[0],
          children: counts.map((measure) => ({
            ...measure,
            description: measure?.measure_description || measure?.description,
            entity_id: measure?.measure_entity_id || measure?.entity_id,
            count: isNil(measure?.count) ? null : measure?.count,
            isMeasureRow: true,
            [INTERNAL_GRID_ROW_ID]: `local${generateId()}`,
            isLeaf: true,
            inventory_item_entity_id: get(head(newChildren), ENTITY_ID_PATH),
          })),
          [groupName]: name,
          error: some(newChildren, { error: true }),
          isInventoryItemGroupRow: true,
        };
      }

      return {
        children: newChildren,
        [groupName]: name,
        isMinorCategoryRow: path === 'reporting_category_minor.name',
        [INTERNAL_GRID_ROW_ID]: `local${generateId()}`,
        error: some(newChildren, { error: true }),
      };
    });
  }

  public updateInventoryItemVenues(items: IQohInventoryItemVenue[]): void {
    const processedInventoryItemVenues = this.processInventoryItemVenues(items);
    let treeData = [];
    this.inventoryCountService.itemList = map(
      processedInventoryItemVenues,
      (item) => {
        return {
          name: item.name,
          inventory_item_entity_id: item.entity_id,
        };
      },
    );
    setTimeout(() => this.columns$.next(this.getInventoryCountColumns()), 0);
    if (!this.isCategoryListView()) {
      this.listViewItems = [];
      const majorItems = [];
      const minorItems = [];

      forEach(processedInventoryItemVenues, (item, index) => {
        if (
          !find(majorItems, { value: item.reporting_category_major.entity_id })
        ) {
          majorItems.push({
            text: item.reporting_category_major.name,
            value: item.reporting_category_major.entity_id,
          });
        }
        if (
          !find(minorItems, { value: item.reporting_category_minor.entity_id })
        ) {
          minorItems.push({
            text: item.reporting_category_minor.name,
            value: item.reporting_category_minor.entity_id,
          });
        }

        this.listViewItems.push({
          ...item,
          id: `${index}`,
          [INTERNAL_GRID_ROW_ID]: `local${generateId()}`,
        });
        forEach(item.measures, (measure, measureIndex) => {
          this.listViewItems.push({
            ...item,
            measure,
            id: `${index}-${measureIndex}`,
            isLeaf: true,
            isLast: isEqual(size(item.measures) - 1, measureIndex),
            [INTERNAL_GRID_ROW_ID]: `local${generateId()}`,
          });
        });
      });
      this.majorItems$.next(majorItems);
      this.minorItems$.next(minorItems);
      this.listViewItems = sortBy(this.listViewItems, (item) =>
        toLower(item.name),
      );
      this.applyColumnState();
    } else {
      treeData = this.convertInventoryItemVenuesToTree(
        processedInventoryItemVenues,
      );
    }

    this.inventoryCountService.initialGridItems = cloneDeep(
      processedInventoryItemVenues,
    );

    setFormFieldValue(
      this.form,
      this.formFieldPath.inventoryCountItems,
      this.isCategoryListView() ? treeData : this.listViewItems,
      false,
    );
    getFormControl(
      this.form,
      this.formFieldPath.inventoryCountItems,
    )?.updateValueAndValidity({
      onlySelf: true,
      emitEvent: true,
    });
    this.isInventoryItemVenuesGridBusy$.next(false);
    this.uiActions.preventDisableLoaderAfterRequestFinish(false);
    this.uiActions.preventLoaderCloseWhenTimeout(false);
    this.uiActions.disableLoader();
    this.handleGridButtonsDisableStatus();
  }

  public processInventoryItemVenues(
    items: IQohInventoryItemVenue[],
  ): IQohInventoryItemVenue[] {
    const cloneItems = cloneDeep(items);
    const mapperItem = this.getUpdatedItemsLevel();

    return map(cloneItems, (item) => {
      item._id = '';

      return Object.assign({}, item, {
        error: mapperItem[item.entity_id]
          ? mapperItem[item.entity_id].error
          : false,
        _id: mapperItem[item.entity_id] ? mapperItem[item.entity_id]._id : '',
      });
    });
  }

  public getUpdatedItemsLevel(): ITransferErrorMapper {
    const savedInventoryItemVenues = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualInventoryCountItems,
    );
    const mapper = reduce(
      savedInventoryItemVenues,
      (itemMap, item) => {
        return {
          ...itemMap,
          [item.item_entity_id]: {
            error: item.error ?? false,
            _id: item._id,
          },
        };
      },
      {},
    );

    return mapper;
  }

  public convertInventoryItemVenuesToTree(
    items: IQohInventoryItemVenue[],
  ): ITreeData[] {
    return this.getSortedTreeDataByConfig(items, {
      path: 'reporting_category_major.name',
      groupName: 'name',
      childrenConfig: {
        path: 'reporting_category_minor.name',
        groupName: 'name',
        childrenConfig: {
          path: 'name',
          groupName: 'name',
          childrenConfig: {
            path: 'measures',
          },
        },
      },
    });
  }

  public isClosedStatus(): boolean {
    return (
      this.inventoryCountGroup?.status ===
      INVENTORY_COUNT_STATUS_VALUE.Completed
    );
  }

  public extendInventoryItemData(params: {
    inventoryItems: IQohInventoryItemVenue[];
    virtualInventoryItems: IInventoryCountItem[];
    qohData: IQuantityOnHandResponse;
    siteId: ObjectId;
  }): IQohInventoryItemVenue[] {
    const { inventoryItems, virtualInventoryItems, qohData, siteId } = params;

    return map(inventoryItems, (item): IQohInventoryItemVenue => {
      const virtualItem = find(
        virtualInventoryItems,
        (vItem) => vItem.inventory_item_entity_id === item.entity_id,
      );
      const sameInventoryItemVenues = filter(
        qohData.items,
        (qohItem) => qohItem.inventory_item_entity_id === item.entity_id,
      );
      const inventoryItemCost = this.getInventoryItemCost(virtualItem, item);
      const managerCount = this.calculateManagerCount(virtualItem, item);
      const countedValue = inventoryItemCost.mul(managerCount);
      const systemCount = new DecimalNumber(
        get(
          virtualItem,
          'system_count',
          get(
            find(
              sameInventoryItemVenues,
              (qohItem) => qohItem?.site_id === siteId,
            ),
            'count',
            0,
          ),
        ),
      );
      const extendedValue = systemCount.mul(inventoryItemCost);
      const varianceQuantity = managerCount.sub(systemCount);
      const varianceCost = inventoryItemCost.mul(varianceQuantity);

      const counts: any[] | IInventoryCountItemChild[] = get(
        virtualItem,
        'counts',
        [],
      );

      // Get item measures from the item
      const itemMeasures = get(item, 'measures', []);

      // Determine which measures to use based on isCompanyLevel flag
      let physicalCountMeasures: any[] | IInventoryCountItemChild[];
      if (InventoryCountGroupService.isCompanyLevel) {
        // New logic: filter measures by transactions.physical_count === true
        physicalCountMeasures =
          this.filterMeasuresByPhysicalCount(itemMeasures);
      } else {
        // Old logic: use physical_count_measures directly
        physicalCountMeasures = get(item, 'physical_count_measures', []);
      }

      const measures = isEmpty(counts)
        ? physicalCountMeasures
        : this.mergeCountsAndMeasures(counts, physicalCountMeasures);

      const hasAtLeastOneCount = some(
        measures,
        (measure) => !isNil(measure.count) && Number(measure.count) >= 0,
      );

      return assign({}, item, {
        measures,
        qohItems: sameInventoryItemVenues,
        ...this.returnMultiplyCountUnit(itemMeasures),
        inventory_item_cost: inventoryItemCost,
        system_count: systemCount,
        manager_count: hasAtLeastOneCount ? managerCount : 0,
        counted_value: hasAtLeastOneCount ? countedValue : 0,
        extended_value: hasAtLeastOneCount ? extendedValue : 0,
        variance_cost: hasAtLeastOneCount ? varianceCost : 0,
        variance_quantity: hasAtLeastOneCount ? varianceQuantity : 0,
      });
    });
  }

  public getQuantityOnHandData(
    inventoryItemVenues: IQohInventoryItemVenue[],
    siteId: string,
  ): Observable<IQohInventoryItemVenue[]> {
    const virtualInventoryItemVenues = getFormFieldValue(
      this.form,
      this.formFieldPath.virtualInventoryCountItems,
    );
    const qohRequestData: IQuantityOnHandItem[] = [
      {
        site_id: siteId,
        inventory_item_entity_ids: map(inventoryItemVenues, ENTITY_ID_PATH),
      },
    ];

    return this.qohService.batchFetchQuantityOnHand(qohRequestData).pipe(
      rx.map((qohRes): IQohInventoryItemVenue[] =>
        this.extendInventoryItemData({
          inventoryItems: inventoryItemVenues,
          virtualInventoryItems: virtualInventoryItemVenues,
          qohData: qohRes,
          siteId,
        }),
      ),
      rx.map((items: IQohInventoryItemVenue[]) => {
        // When isCompanyLevel is true, filter out items with no physical count measures
        // to keep behavior consistent with the old logic's API-level filter
        if (!InventoryCountGroupService.isCompanyLevel) {
          return items;
        }

        return filter(items, (item) => !isEmpty(get(item, 'measures', [])));
      }),
      rx.catchError((error: any): ObservableInput<any> => {
        const {
          meta: { message },
          title,
        } = error;
        const toast = {
          errors: {
            title,
            message,
          },
        };

        this.isInventoryItemVenuesGridBusy$.next(false);
        this.uiActions.showErrorToasterMessage(toast);

        return observableOf(false);
      }),
    );
  }

  public mergeCountsAndMeasures(
    counts: IInventoryCountItemChild[],
    measures: IInventoryCountItemChild[],
  ): IInventoryCountItemChild[] {
    const mergedCountsAndMeasures = [];
    counts.forEach((count) => {
      const measureToMerge = find(
        measures,
        (measure) => measure.entity_id === count.measure_entity_id,
      );
      if (!isEmpty(measureToMerge)) {
        mergedCountsAndMeasures.push({ ...count, ...measureToMerge });
      }
    });

    forEach(measures, (measure) => {
      const isMerged = mergedCountsAndMeasures.some(
        (item) => item.measure_entity_id === measure.entity_id,
      );
      if (!isMerged) {
        mergedCountsAndMeasures.push(measure);
      }
    });

    return mergedCountsAndMeasures;
  }

  public getInventoryItemCost(
    virtualItem: IInventoryCountItem,
    item?: IQohInventoryItemVenue,
  ): DecimalNumber {
    const cost = find(item?.costs, 'is_preferred');
    const costValue = get(cost, 'cost_value', virtualItem?.cost);

    return new DecimalNumber(costValue);
  }

  public calculateManagerCount(
    virtualItem: IInventoryCountItem,
    item?: IQohInventoryItemVenue,
  ): DecimalNumber {
    const factorValues = map(
      get(virtualItem, 'counts', item?.measures || []),
      (measure) => measure.factor_value * (measure?.count || 0),
    );

    return factorValues.reduce(
      (partialSum, a) => partialSum.add(a),
      new DecimalNumber(0),
    );
  }

  public fetchSiteAndInventoryCountGroup(): Observable<ISiteInventoryCountGroup> {
    return observableCombineLatest([
      this.sitesService.fetchList({
        filter: `_id eq in(${this.document?.site_id || ''})`,
        select: ['name', 'id', 'store_number', 'metadata'],
        filter_by_assigned_sites: true,
      }),
      this.inventoryCountGroupService.fetchList({
        // commented since there is an issue that we got the empty array when filter by entity_id
        // filter: `entity_id eq in(${this.document?.inventory_count_group_entity_id || ''})`,
        select: ['name', 'entity_id', 'status'],
      }),
    ]).pipe(
      rx.map(([sitesResponse, groupResponse]) => {
        return {
          site: sitesResponse?.items[0] as ISite,
          inventoryCountGroup:
            groupResponse?.items.find(
              (item) =>
                item.entity_id ===
                this.document?.inventory_count_group_entity_id,
            ) || {},
        };
      }),
    );
  }

  public numberValueFormatter(
    params: ValueFormatterParams,
    isCurrency: boolean,
  ): string | null {
    return !isUndefined(params?.value)
      ? this.numberFormatService.getNumberFormatter(isCurrency)(params)
      : null;
  }

  public descriptionValueFormatter(params: ValueFormatterParams): string {
    let description = params?.data?.description;
    let factorValue = params?.data?.factor_value;
    if (!this.isCategoryListView()) {
      description = get(
        params,
        ['data', 'measure', 'description'],
        get(params, ['data', 'measure', 'measure_description']),
      );
      factorValue = get(params, ['data', 'measure', 'factor_value']);
    }

    return description && factorValue
      ? `${description} [${this.numberFormatService.getNumberFormatter(false)({ value: factorValue })}]`
      : '';
  }

  public countCellRendererSelector(
    params: ICellRendererParams,
  ): ComponentSelectorResult | null {
    return !this.isClosedStatus() &&
      (!isUndefined(params.value) || !this.isCategoryListView())
      ? { component: CELL_RENDERER.float_input_renderer }
      : null;
  }

  public countValueFormatter(params: ICellRendererParams): string {
    return !isNil(params?.value) ? params.value : '';
  }

  public getInlineChangeMapper(): (
    value: IInventoryCountItem,
    payload: XuxGridActionPayload,
  ) => GridRowData {
    return (value): GridRowData => {
      if (this.isCategoryListView()) {
        return value;
      }
      this.curDisplayItems = getAllRowsAfterFilterAndSort(
        this.listGrid?.xuxGridComponent?.gridApi,
      );

      return this.calculationData(value);
    };
  }

  public getInlineChangeMapperItemForTree(): (
    value: IInventoryCountItem,
    payload: XuxGridActionPayload,
    treeData: ITreeData[],
  ) => ITreeData[] {
    return (value, payload, treeData): ITreeData[] => {
      const parentRowId = get(payload, ['rowNode', 'parent', 'id']);

      return map(treeData, (majorItem: ITreeData) => {
        const majorChildren =
          (get(majorItem, 'children', []) as ITreeData[]) || [];

        return {
          ...majorItem,
          children: map(majorChildren, (minorItem: ITreeData) => {
            const minorChildren =
              (get(minorItem, 'children', []) as IInventoryCountItem[]) || [];

            return {
              ...minorItem,
              children: map(minorChildren, (item: IInventoryCountItem) =>
                get(item, [INTERNAL_GRID_ROW_ID]) === parentRowId
                  ? { ...item, ...this.calculationData(item) }
                  : item,
              ),
            };
          }),
        };
      });
    };
  }

  public handleUpdateOtherGridItem(
    payload: XuxGridAction['payload'],
    updatedItems: GridRowData[],
  ): XuxGridAction['payload'] {
    const parentRowId = get(payload, ['rowNode', 'parent', 'id']);
    let newRowValue: GridRowData = {};
    // since the grid is in tree data structure,
    // we need to loop through the major and minor category to
    // find the updated item to update the value in the tree data,
    // so we can keep the expand/collapse status after update
    some(updatedItems as ITreeData[], (majorItem: ITreeData) =>
      some(get(majorItem, 'children', []), (minorItem: ITreeData) =>
        some(get(minorItem, 'children', []), (item) => {
          if (get(item, [INTERNAL_GRID_ROW_ID]) === parentRowId) {
            newRowValue = item;

            return true;
          }

          return false;
        }),
      ),
    );

    return [
      {
        field: payload.field,
        rowId: parentRowId,
        value: newRowValue,
      },
    ];
  }

  public getInlineChangeMapperItemsForList(): (
    value: IInventoryCountItem,
    payload: XuxGridActionPayload,
  ) => GridRowData[] {
    return (value): GridRowData[] => {
      const newItems = [];

      const dataItem = find(this.listViewItems, { id: value.id });

      forEach(this.curDisplayItems, (item) => {
        if (
          isNil(get(item, 'data.isLeaf')) &&
          get(item, 'data.entity_id') === value.entity_id
        ) {
          const data = item.data;
          const hasAtLeastOneCount = some(
            item?.data?.measures,
            (measure) => !isNil(measure.count) && Number(measure.count) >= 0,
          );

          newItems.push({
            rowId: item.id,
            index: item.rowIndex,
            value: {
              ...data,
              manager_count: hasAtLeastOneCount ? dataItem.manager_count : 0,
              counted_value: hasAtLeastOneCount ? dataItem.counted_value : 0,
              extended_value: hasAtLeastOneCount ? dataItem.extended_value : 0,
              variance_quantity: hasAtLeastOneCount
                ? dataItem.variance_quantity
                : 0,
              variance_cost: hasAtLeastOneCount ? dataItem.variance_cost : 0,
              isChanged: true,
            },
          });
        }
      });

      return newItems;
    };
  }

  public calculationData(value: IInventoryCountItem): GridRowData {
    // system_count qoh
    // managerCount =  reduce (count *  factor_value)
    let managerCount;
    let hasAtLeastOneCount = false;
    if (has(value, 'isLeaf') && !this.isCategoryListView()) {
      managerCount = reduce(
        map(value?.measures, (measure) => {
          if (has(measure, 'count')) {
            return new DecimalNumber(measure.factor_value).mul(
              isNil(measure.count) ? 0 : measure.count,
            );
          }

          return new DecimalNumber(0);
        }),
        (prev, next) => prev.add(next),
        new DecimalNumber(0),
      );
      hasAtLeastOneCount = some(
        value?.measures,
        (measure) => !isNil(measure.count) && Number(measure.count) >= 0,
      );
    } else {
      managerCount = value?.children
        ?.map((measure) =>
          new DecimalNumber(measure.factor_value).mul(
            isNil(measure.count) ? 0 : measure.count,
          ),
        )
        .reduce((prev, next) => prev.add(next));
      hasAtLeastOneCount = some(
        value?.children,
        (measure) => !isNil(measure.count) && Number(measure.count) >= 0,
      );
    }

    let factorValue: number = 1;
    const countUnitValue = get(value, 'uom');
    if (isObject(countUnitValue)) {
      factorValue = get(countUnitValue, 'factor_value');
    }
    managerCount = managerCount.div(factorValue);

    const varianceQuantity = managerCount.sub(value.system_count);

    const newValue = {
      ...value,
      manager_count: hasAtLeastOneCount ? managerCount : 0,
      counted_value: hasAtLeastOneCount
        ? new DecimalNumber(value.inventory_item_cost).mul(managerCount)
        : 0,
      extended_value: hasAtLeastOneCount
        ? new DecimalNumber(value.inventory_item_cost).mul(value.system_count)
        : 0,
      variance_quantity: hasAtLeastOneCount ? varianceQuantity : 0,
      variance_cost: hasAtLeastOneCount
        ? new DecimalNumber(value.inventory_item_cost).mul(varianceQuantity)
        : 0,
      isChanged: true,
    };
    if (!this.isCategoryListView()) {
      forEach(this.listViewItems, (item) => {
        if (value?.id === item.id) {
          set(item, 'manager_count', newValue.manager_count);
          set(item, 'counted_value', newValue.counted_value);
          set(item, 'extended_value', newValue.extended_value);
          set(item, 'variance_quantity', newValue.variance_quantity);
          set(item, 'variance_cost', newValue.variance_cost);
          set(item, 'isChanged', true);
        }
      });
    }

    return newValue;
  }

  public initListTotalsSubscription(): void {
    this.subscriptions.push(this.getHandleListTotalsDataSubs());
  }

  public getHandleListTotalsDataSubs(): Subscription {
    return observableCombineLatest([
      getFormControl(this.form, this.formFieldPath.inventoryCountItems)
        .valueChanges,
    ])
      .pipe(
        rx.distinctUntilChanged(),
        // eslint-disable-next-line import/no-deprecated
        rx.startWith(null),
        rx.map(() =>
          this.handleTotalsData(
            getFormFieldValue(
              this.form,
              this.formFieldPath.inventoryCountItems,
            ),
          ),
        ),
        rx.map((data) =>
          this.mapItemDataToTotalsData(data, this.formFieldPath),
        ),
      )
      .subscribe(this.updateListGridData.bind(this));
  }

  public mapItemDataToTotalsData(
    data: IGridInventoryCountItemTotalData,
    formFieldPath: IInventoryCountFormFieldPath,
  ): IGridInventoryCountItemTotalFormData {
    const pinnedBottomData = this.buildPinnedData(
      this.isCategoryListView()
        ? data.flatItems
        : (data.items as IInventoryCountItem[]),
      formFieldPath,
    );

    return {
      pinnedBottomData,
      normalRows: data.items,
    };
  }

  public updateListGridData(
    totalsData: IGridInventoryCountItemTotalFormData,
  ): void {
    if (isNil(totalsData)) return null;
    // added setTimeout to fix a case when pinned row is not displayed for completed counts
    setTimeout(() =>
      this.listGrid?.xuxGridComponent?.gridApi?.setPinnedBottomRowData(
        totalsData.pinnedBottomData,
      ),
    );
  }

  public buildPinnedData(
    baseOnRows: IInventoryCountItem[],
    formFieldPath: IInventoryCountFormFieldPath,
  ): IGridFlatInventoryCountItemTotal[] | null {
    if (isEmpty(baseOnRows)) {
      // all rows empty, no pinned data
      return null;
    }

    const totalCountedValue = this.getSubTotal(
      baseOnRows,
      formFieldPath.countedValue,
    );
    const totalExtendedValue = this.getSubTotal(
      baseOnRows,
      formFieldPath.extendedValue,
    );
    const totalVarianceCost = this.getSubTotal(
      baseOnRows,
      formFieldPath.varianceCost,
    );

    const pinnedBottomData = {
      name: this.i18nService.instant(I18n.Common.Total),
      counted_value: totalCountedValue,
      extended_value: totalExtendedValue,
      variance_cost: totalVarianceCost,
    };

    return [pinnedBottomData];
  }

  public getSubTotal(
    items: IInventoryCountItem[],
    targetPath: string,
  ): DecimalNumber {
    const hasInput = some(
      items,
      (item) => !isNaN(parseFloat(item?.[targetPath])),
    );
    if (!hasInput) return null;

    return reduce(
      items,
      (pre, cur: IInventoryCountItem) => {
        if (has(cur, 'isLeaf')) {
          return pre;
        }
        const curNumberStr = get(cur, targetPath, 0);
        const curNumber = parseFloat(`${curNumberStr}`);

        return pre.add(isNaN(curNumber) ? 0 : curNumberStr);
      },
      new DecimalNumber(0),
    );
  }

  public handleTotalsData(items: ITreeData): IGridInventoryCountItemTotalData {
    if (isEmpty(items)) return {};

    return {
      items,
      flatItems: flatMapDeep(
        map(items, (majorList) =>
          map(majorList.children, (minorList) => minorList.children),
        ),
      ),
    };
  }

  public getQuantityCellClassRules(
    canEditStatus: INVENTORY_COUNT_ITEM_STATUS_VALUE[],
  ): ColDef['cellClassRules'] {
    return {
      'ag-numeric-cell': (): boolean => true,
      'show-placeholder': ({ value }): boolean => {
        const isValidValue = !(value || value === 0);
        if (
          isEmpty(canEditStatus) ||
          (!isEmpty(canEditStatus) &&
            includes(canEditStatus, this.getFormStatus()))
        ) {
          return isValidValue;
        }

        return false;
      },
    };
  }

  public getFormStatus(): INVENTORY_COUNT_ITEM_STATUS_VALUE {
    return this.getFormFieldValue(this.formFieldPath.status);
  }

  public isCategoryListView(): boolean {
    // When the user does not configure list view or CATEGORY_VIEW, it will return false.
    return isEqual(
      this.inventoryCountService.viewSetting,
      GRID_LIST_VIEW_TYPE.CATEGORY_VIEW,
    );
  }

  public getInventoryCountColumns(): IXVIListColumn[] {
    return this.isCategoryListView()
      ? inventoryCountTreeColumns(this)
      : inventoryCountListColumns(this);
  }

  public isNotLeaf(params: ICellRendererParams): boolean {
    return isNil(get(params, ['data', 'isLeaf']));
  }

  public listViewDisplayValue(
    params: ICellRendererParams,
    isCurrency: boolean,
    needHide: boolean = false,
  ): string {
    const formatterSetting = needHide
      ? this.showFormatterOfCount(params, isCurrency)
      : this.numberValueFormatter(params, isCurrency);

    return this.isNotLeaf(params) ? formatterSetting : '';
  }

  public saveUserConfig(
    moduleName: USER_CONFIG_MODULE_NAME,
    selectColumns: string[] = null,
  ): void {
    if (this.isCategoryListView()) {
      return;
    }
    const userConfig: IUserSetting = this.columnApi.getColumnState();
    if (!isNil(selectColumns)) {
      forEach(userConfig, (colState: ColumnState) => {
        if (
          !includes(selectColumns, colState.colId) &&
          !includes(SELECTABLE_COLUMNS, colState.colId)
        ) {
          colState.hide = colState.hide;
        }
        if (includes(SELECTABLE_COLUMNS, colState.colId)) {
          colState.hide = true;
        }
        if (includes(selectColumns, colState.colId)) {
          colState.hide = false;
        }
      });
    }
    this.columnApi.setColumnState(userConfig);
    const targetModuleConfig = findColumnConfig(
      this.allModuleColumnConfig,
      moduleName,
    );
    if (isNil(targetModuleConfig)) {
      this.allModuleColumnConfig?.push({
        module_type: USER_CONFIG_MODULE_TYPE.INVENTORY_COUNT,
        module_name: moduleName,
        module_config: {
          ...get(this.userViewConfig, 'module_config', {}),
          alphabetical_list_view: userConfig,
        },
      });
    } else {
      targetModuleConfig.module_config = {
        ...get(this.userViewConfig, 'module_config', {}),
        alphabetical_list_view: userConfig,
      };
    }
    const subscription = this.toolsService
      .saveUserConfig(
        isNil(targetModuleConfig)
          ? USER_CONFIG_MODULE_TYPE.INVENTORY_COUNT
          : targetModuleConfig.module_type,
        moduleName,
        {
          ...get(this.userViewConfig, 'module_config', {}),
          alphabetical_list_view: userConfig,
        },
      )
      .subscribe();
    this.subscriptions.push(subscription);
  }

  public autoAdjustMovedColumn(
    index: number,
    colId: string,
    source: string,
  ): void {
    if (source !== 'uiColumnDragged') {
      return;
    }
    this.columnApi.moveColumn(colId, index);
  }

  public showFormatterOfCount(
    params: ICellRendererParams,
    isCurrency: boolean,
  ): string | null {
    const hasCount = some(
      this.isCategoryListView()
        ? params?.data?.children
        : params?.data?.measures,
      (measure) => !isNil(measure.count) && Number(measure.count) >= 0,
    );

    const isTotalRow = isEqual(params?.data?.name, this.totalName);
    const needShowValue =
      isTotalRow || hasCount
        ? this.numberFormatService.getNumberFormatter(isCurrency)(params)
        : '';

    return !isUndefined(params?.value) ? needShowValue : null;
  }

  // when write count not nil, should patch data to fix add or plus button can not work.
  public patchInventoryItemsInTreeView(
    flattenedItems: Partial<IInventoryItemVenue>[],
    overrideValue: number,
  ): Partial<IInventoryItemVenue>[] {
    const patchedItems = map(flattenedItems, (item) => {
      const itemChildren = get(item, 'children', []);

      forEach(itemChildren, (childItem, childIndex) => {
        const count = get(item, ['children', childIndex, 'count']);
        if (isNil(count)) {
          set(item, ['children', childIndex, 'count'], overrideValue);
        } else {
          const previousCount = get(item, ['children', childIndex, 'count']);
          unset(item, `children.${childIndex}.count`);
          set(
            item,
            ['children', childIndex, 'count'],
            previousCount || overrideValue,
          );
        }
      });

      return { ...this.calculationData(item), isChanged: item?.isChanged };
    });

    return patchedItems;
  }

  // when write count not nil, should patch data to fix each grid total row calculate error.
  public patchInventoryItemsInListView(
    flattenedItems: Partial<IInventoryItemVenue>[],
  ): Partial<IInventoryItemVenue>[] {
    return map(flattenedItems, (item) => {
      if (!item?.isLeaf) {
        const leafNode = find(flattenedItems, {
          entity_id: item?.entity_id,
          isLeaf: true,
        });
        set(item, 'variance_quantity', get(leafNode, 'variance_quantity'));
        set(item, 'variance_cost', get(leafNode, 'variance_cost'));
        set(item, 'extended_value', get(leafNode, 'extended_value'));
        set(item, 'counted_value', get(leafNode, 'counted_value'));
        set(item, 'manager_count', get(leafNode, 'manager_count'));
      }
      if (item?.isLeaf) {
        return { ...this.calculationData(item), isChanged: item?.isChanged };
      }

      return item;
    });
  }

  // build needed data
  public updateInventoryItemData(
    inventoryCountItems: Partial<IInventoryItemVenue>[],
    { overrideValue, overrideType }: IInventoryCountOverrideValueOptions,
  ): Partial<IInventoryItemVenue>[] {
    const isOverrideAll = isEqual(
      overrideType,
      INVENTORY_COUNT_OVERRIDE_VALUE_TYPES.ALL,
    );
    const isCategoryListView = this.isCategoryListView();
    let flattenCountItems: Partial<IInventoryItemVenue>[] = [];
    if (isCategoryListView) {
      flattenCountItems = flattenItems(inventoryCountItems);

      const updatedItems = map(flattenCountItems, (item) => {
        const reportingCategoryMajor = {
          entity_id: item?.reporting_category_major?.entity_id,
          name: item?.reporting_category_major?.name,
        };
        const reportingCategoryMinor = {
          entity_id: item?.reporting_category_minor?.entity_id,
          name: item?.reporting_category_minor?.name,
        };

        const counts = map(item?.children, (count) => {
          const previousCount = get(count, 'count');
          const currentCount = this.handleValueOverride(
            overrideType,
            overrideValue,
            previousCount,
          );

          if (!isEqual(previousCount, currentCount)) {
            item.isChanged = true;
          }

          return {
            measure_description: count.description,
            measure_entity_id: count.entity_id,
            count: currentCount,
            factor_value: count.factor_value,
          };
        });
        let overrideProperties = {};
        if (!item?.isLeaf && isOverrideAll) {
          overrideProperties = {
            variance_quantity: null,
            variance_cost: null,
            extended_value: null,
            counted_value: null,
            manager_count: null,
          };
        }

        return {
          ...item,
          entity_id: item?.entity_id || generateObjectId(),
          site_id: item?.site_id || this.document?.site_id,
          inventory_count_entity_id: this.document?.entity_id,
          inventory_item_entity_id: item?.entity_id,
          name: item?.name || '',
          description: item?.description || '',
          system_count: item?.system_count || 0,
          variance_count: item?.variance_count || 0,
          cost: item?.inventory_item_cost || 0,
          variance_cost: item?.variance_cost || 0,
          counts: counts || [],
          costs: item?.costs,
          inventory_item_cost: item?.inventory_item_cost,
          reporting_category_major: reportingCategoryMajor,
          reporting_category_minor: reportingCategoryMinor,
          isChanged: item?.isChanged,
          ...overrideProperties,
        };
      });
      if (!isNil(overrideValue)) {
        return this.patchInventoryItemsInTreeView(updatedItems, overrideValue);
      }

      return updatedItems;
    } else {
      const updatedItems = map(inventoryCountItems, (item) => {
        if (item?.measure) {
          const previousCount = get(item, ['measure', 'count']);
          const currentCount = this.handleValueOverride(
            overrideType,
            overrideValue,
            previousCount,
          );
          if (!isEqual(previousCount, currentCount)) {
            item.measure.count = currentCount;
            item.isChanged = true;
          }

          return item;
        }
        if (!item?.isLeaf && isOverrideAll) {
          return {
            ...item,
            variance_quantity: null,
            variance_cost: null,
            extended_value: null,
            counted_value: null,
            manager_count: null,
          };
        }

        return item;
      });

      if (!isNil(overrideValue)) {
        return this.patchInventoryItemsInListView(updatedItems);
      }

      return updatedItems;
    }
  }

  public removeCountProperty(
    inventoryCountItems: Partial<IInventoryItemVenue>[],
  ): Partial<IInventoryItemVenue>[] {
    forEach(inventoryCountItems, (item) => {
      const itemChildren = get(item, 'children', []);
      forEach(itemChildren, (childItem, childIndex) => {
        unset(item, `children.${childIndex}.count`);
      });

      return item;
    });

    return inventoryCountItems;
  }

  public handleValueOverride(
    overrideType: INVENTORY_COUNT_OVERRIDE_VALUE_TYPES,
    overrideValue: IInventoryCountValue,
    previousCount: IInventoryCountValue,
  ): IInventoryCountValue {
    let currentCount;

    switch (overrideType) {
      case INVENTORY_COUNT_OVERRIDE_VALUE_TYPES.ALL:
        currentCount = overrideValue;
        break;
      case INVENTORY_COUNT_OVERRIDE_VALUE_TYPES.NIL:
        currentCount = isNil(previousCount) ? overrideValue : previousCount;
        break;
      default:
        currentCount = previousCount;
        break;
    }

    return currentCount;
  }

  public async clearAllCount(): Promise<void> {
    const { confirmed }: IModalDialogResult =
      await this.uiActions.promptFormModal(clearCountModalOptions);
    const inventoryCountItems: Partial<IInventoryItemVenue>[] =
      this.getFormFieldValue('inventory_count_items');
    if (!confirmed) {
      return;
    }

    const isCategoryListView = this.inventoryCountService.isCategoryListView();
    const flattenedItems = this.updateInventoryItemData(inventoryCountItems, {
      overrideValue: null,
      overrideType: INVENTORY_COUNT_OVERRIDE_VALUE_TYPES.ALL,
    });

    const noneCountPropertyItems = this.removeCountProperty(flattenedItems);

    setFormFieldValue(
      this.form,
      this.formFieldPath.inventoryCountItems,
      isCategoryListView
        ? this.convertInventoryItemVenuesToTree(
            noneCountPropertyItems as unknown as IQohInventoryItemVenue[],
          )
        : noneCountPropertyItems,
    );

    // when clear all may cause grid unexpanded.
    this.listGridApi.expandAll();
  }

  // XVI-9956: only need write count as 0 and override the count which is nil.
  public writeCountsToBlankCounts(): void {
    const overrideCount: number = 0;
    const inventoryCountItems: IInventoryItemVenue[] = this.getFormFieldValue(
      'inventory_count_items',
    );
    const flattenedItems = this.updateInventoryItemData(inventoryCountItems, {
      overrideValue: overrideCount,
      overrideType: INVENTORY_COUNT_OVERRIDE_VALUE_TYPES.NIL,
    });

    setFormFieldValue(
      this.form,
      'inventory_count_items',
      this.inventoryCountService.isCategoryListView()
        ? this.convertInventoryItemVenuesToTree(flattenedItems as any)
        : flattenedItems,
    );

    this.listGridApi.expandAll();
  }

  public handleGridButtonsDisableStatus(): boolean {
    const currentInventoryCountStatus = this.getFormFieldValue('status');
    const enableButtonsStatus = [
      INVENTORY_COUNT_STATUS_VALUE.Open,
      INVENTORY_COUNT_STATUS_VALUE.Closed,
    ];
    const isEmptyInventoryCountItems = isEmpty(
      getFormFieldValue(this.form, this.formFieldPath.inventoryCountItems),
    );
    const canManage = this.permissionService.canManage(
      ENTITY_TYPES.INVENTORY_COUNT,
    );

    this.isFinalStatus$.next(
      currentInventoryCountStatus === INVENTORY_COUNT_STATUS_VALUE.Completed,
    );
    this.isDisableGridButtons$.next(
      !canManage ||
        !includes(enableButtonsStatus, currentInventoryCountStatus) ||
        isEmptyInventoryCountItems,
    );

    return (
      !canManage ||
      !includes(enableButtonsStatus, currentInventoryCountStatus) ||
      isEmptyInventoryCountItems
    );
  }

  public getReallyChangeGridItems(
    updatedItems: InventoryCountCommonGridRow[],
  ): InventoryCountCommonGridRow[] {
    return filter(
      map(updatedItems, (updatedItem: InventoryCountCommonGridRow) => {
        const matchedInitialGridItem = find(
          this.inventoryCountService.initialGridItems,
          {
            entity_id: updatedItem.entity_id,
          },
        );

        if (matchedInitialGridItem) {
          const hasCountChangedMeasure = some(
            this.isCategoryListView()
              ? updatedItem.children
              : updatedItem.measures,
            (updatedMeasure: InventoryCountGridMeasure) => {
              const matchedInitialMeasure = find(
                matchedInitialGridItem.measures,
                { entity_id: updatedMeasure.entity_id },
              );

              return (
                matchedInitialMeasure &&
                (!isNil(updatedMeasure.count) ||
                  !isNil(matchedInitialMeasure.count)) &&
                !isEqual(updatedMeasure.count, matchedInitialMeasure.count)
              );
            },
          );

          if (hasCountChangedMeasure) {
            return updatedItem;
          }
        }

        return null;
      }),
      (item: InventoryCountCommonGridRow | null) => !isEmpty(item),
    );
  }

  public showUOMColumnValue(params: ICellRendererParams): string {
    const UOMValue: string = isObject(params.value)
      ? `${get(params.value, 'description')} [${this.numberFormatService.getNumberFormatter(false)({ value: get(params.value, 'factor_value') })}]`
      : params.value;

    return this.isNotLeaf(params) ? UOMValue : '';
  }

  // eslint-disable-next-line max-lines
}
